import { createAsyncThunk } from '@reduxjs/toolkit';
import { captureException } from '@sentry/browser';
import instance from '../../axiosInstance';
import Agent from '../../interfaces/agent';
import { deserializeData } from '../../utils/deserializer';
import type { State } from '..';
import { fetchAgentTags } from './tags';
import {
  AgentLiveTransferRequest,
  AgentLiveTransferRequestDisposition,
} from './types';

/**
 * Tags are auxiliary: a failure of the tags endpoint must not fail the agent
 * load or save. Report it and fall back to the tags on the agent payload.
 */
const fetchAgentTagsOrFallback = (agentId: string | number) =>
  fetchAgentTags(agentId).catch((error) => {
    captureException(error);
    return undefined;
  });

/// /
// Async Thunk Actions
/// /
export const fetchAgentById = createAsyncThunk<Agent, string | number>(
  'agents/fetchAgentById',
  async (agentId) => {
    const [response, tags] = await Promise.all([
      instance.get(`/deals/agent/${agentId}/`),
      fetchAgentTagsOrFallback(agentId),
    ]);
    return { ...response.data, tags: tags ?? response.data.tags };
  },
);

export const patchAgent = createAsyncThunk<Agent, any, { state: State }>(
  'agents/patchAgent',
  async (agent, { getState }) => {
    const agentId = agent.id;
    const { data } = agent;
    const response = await instance.patch(`/deals/agent/${agentId}/`, {
      ...data,
    });

    // Tag writes still go through /deals/agent. Only when this patch changed
    // tags do we re-read them from the tags endpoint; otherwise keep the tags
    // already in the store so /deals/agent's copy doesn't replace them.
    if (data && 'tags' in data) {
      const tags = await fetchAgentTagsOrFallback(agentId);
      return { ...response.data, tags: tags ?? response.data.tags };
    }

    const storedTags = getState().agents.agentsMap[agentId]?.tags;
    return { ...response.data, tags: storedTags ?? response.data.tags };
  },
);

export const fetchAgentLiveTransferRequestsByDealId = createAsyncThunk<
  AgentLiveTransferRequest[],
  { dealId: string }
>('agents/fetchAgentLiveTransferRequestsByDealId', async ({ dealId }) => {
  const response = await instance.get(
    `v4/agent-live-transfer/requests/?filter[deal]=${dealId}&page[size]=100`,
  );

  const data = deserializeData<AgentLiveTransferRequest[]>(response.data);

  if (!data || !data.length) {
    return [];
  }

  return data;
});

export const fetchAgentLiveTransferRequestDispositions = createAsyncThunk<
  AgentLiveTransferRequestDisposition[]
>('agents/fetchAgentLiveTransferRequestDispositions', async () => {
  const response = await instance.get(
    `/v4/agent-live-transfer/request-dispositions/`,
  );

  const data = deserializeData<AgentLiveTransferRequestDisposition[]>(
    response.data,
  );

  if (!data || !data.length) {
    return [];
  }

  return data;
});
